import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import ReactFlow, { Background, Controls, ReactFlowProvider, useReactFlow } from "reactflow";
import "reactflow/dist/style.css";
import api, { formatApiError } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { FolderSelect } from "@/components/FolderSelect";
import { MoveMapNode } from "@/components/MoveMapNode";
import { MovementForm } from "@/components/MovementForm";
import { buildMoveMap, yearsFromMovements, fmtMoney, CURRENCY_SYMBOL, parseLocalDate } from "@/lib/movemap";
import { useTheme } from "@/context/ThemeContext";
import { PAGE, BTN_PRIMARY, ICON_BTN } from "@/lib/styles";
import { ArrowLeft, Save, Plus, Trash2, Pencil, Wallet, ListTree, Undo2, Redo2 } from "lucide-react";
import { toast } from "sonner";

const nodeTypes = { movenode: MoveMapNode };

function Editor({ id }) {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { fitView } = useReactFlow();
  const [title, setTitle] = useState("");
  const [folderId, setFolderId] = useState("");
  const [movements, setMovements] = useState([]);
  const [displayCurrency, setDisplayCurrency] = useState("USD");
  const [rate, setRate] = useState(0);
  const [year, setYear] = useState(new Date().getFullYear());
  const [createdAt, setCreatedAt] = useState(null);
  const [history, setHistory] = useState([]);
  const [saving, setSaving] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [listOpen, setListOpen] = useState(true);
  const loaded = useRef(false);

  // --- Historial Deshacer/Rehacer ---
  const movRef = useRef([]);
  useEffect(() => { movRef.current = movements; }, [movements]);
  const pastRef = useRef([]);
  const futureRef = useRef([]);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const syncHist = useCallback(() => { setCanUndo(pastRef.current.length > 0); setCanRedo(futureRef.current.length > 0); }, []);
  const snapshot = useCallback(() => {
    pastRef.current.push(movRef.current);
    if (pastRef.current.length > 60) pastRef.current.shift();
    futureRef.current = [];
    syncHist();
  }, [syncHist]);
  const undo = useCallback(() => {
    if (!pastRef.current.length) return;
    futureRef.current.push(movRef.current);
    setMovements(pastRef.current.pop());
    syncHist();
  }, [syncHist]);
  const redo = useCallback(() => {
    if (!futureRef.current.length) return;
    pastRef.current.push(movRef.current);
    setMovements(futureRef.current.pop());
    syncHist();
  }, [syncHist]);
  useEffect(() => {
    const onKey = (e) => {
      if (["INPUT", "TEXTAREA", "SELECT"].includes(e.target.tagName)) return;
      if ((e.ctrlKey || e.metaKey) && !e.shiftKey && e.key.toLowerCase() === "z") { e.preventDefault(); undo(); }
      else if ((e.ctrlKey || e.metaKey) && (e.key.toLowerCase() === "y" || (e.shiftKey && e.key.toLowerCase() === "z"))) { e.preventDefault(); redo(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [undo, redo]);

  useEffect(() => {
    let ignored = false;
    api.get(`/notes/${id}`).then(({ data }) => {
      if (ignored) return;
      setTitle(data.title);
      setFolderId(data.folder_id || "");
      setCreatedAt(data.created_at);
      const mv = data.movemap || {};
      setMovements(mv.movements || []);
      setDisplayCurrency(mv.displayCurrency || "USD");
      if (mv.rate) setRate(mv.rate);
      loaded.current = true;
    }).catch((err) => {
      if (ignored) return;
      toast.error(formatApiError(err.response?.data?.detail));
      navigate("/");
    });
    return () => { ignored = true; };
  }, [id, navigate]);

  // Tasa por defecto desde /api/rates (Dólar BCV) si aún no hay una guardada.
  useEffect(() => {
    api.get("/rates").then(({ data }) => {
      const usd = (data.assets || []).find((a) => a.code === "usd_bcv");
      if (usd?.rate) setRate((prev) => prev || usd.rate);
    }).catch(() => {});
  }, []);

  // Histórico del BCV para convertir movimientos con fechas pasadas a su tasa de ese día.
  useEffect(() => {
    api.get("/rates/history").then(({ data }) => setHistory(data.history || [])).catch(() => {});
  }, []);

  const years = useMemo(() => yearsFromMovements(movements, createdAt), [movements, createdAt]);
  useEffect(() => { if (years.length && !years.includes(year)) setYear(years[0]); }, [years, year]);

  const { nodes, edges, totals } = useMemo(
    () => buildMoveMap({ movements, year, displayCurrency, rate, createdAt, history }),
    [movements, year, displayCurrency, rate, createdAt, history]
  );

  useEffect(() => { const t = setTimeout(() => fitView({ padding: 0.2, duration: 300 }), 120); return () => clearTimeout(t); }, [nodes, fitView]);

  const saveMovement = (m) => {
    setMovements((prev) => {
      const exists = prev.some((x) => x.id === m.id);
      return exists ? prev.map((x) => (x.id === m.id ? m : x)) : [...prev, m];
    });
    if (m.fecha) setYear(parseLocalDate(m.fecha).getFullYear());
    setFormOpen(false);
    setEditing(null);
  };

  const removeMovement = (mid) => {
    if (!window.confirm("¿Eliminar este movimiento?")) return;
    snapshot();
    setMovements((prev) => prev.filter((x) => x.id !== mid));
  };

  const save = useCallback(async () => {
    setSaving(true);
    try {
      await api.put(`/notes/${id}`, {
        title: title || "Mapa de flujo económico",
        movemap: { displayCurrency, rate, movements },
        folder_id: folderId || null,
      });
      toast.success("Guardado");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  }, [id, title, displayCurrency, rate, movements, folderId]);

  const bgColor = theme === "dark" ? "#1e293b" : "#e2e8f0";
  const monthList = movements
    .filter((m) => m.fecha && parseLocalDate(m.fecha).getFullYear() === year)
    .sort((a, b) => parseLocalDate(b.fecha) - parseLocalDate(a.fecha));

  return (
    <div className={`${PAGE} font-body flex flex-col h-screen`}>
      <Navbar />
      <div className="flex items-center gap-3 px-4 py-3 border-b border-slate-200 dark:border-slate-800 flex-wrap">
        <button onClick={() => navigate("/")} className={ICON_BTN} data-testid="movemap-back"><ArrowLeft size={16} /></button>
        <input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          className="font-heading font-medium text-lg bg-transparent border-none focus:outline-none focus:ring-0 min-w-[160px] flex-1"
          placeholder="Mapa de flujo económico"
          data-testid="movemap-title"
        />
        <FolderSelect value={folderId} onChange={setFolderId} allowNew />

        <div className="flex items-center gap-1 border border-slate-200 dark:border-slate-800 rounded-lg p-0.5" data-testid="movemap-currency">
          {["USD", "VES"].map((c) => (
            <button key={c} onClick={() => setDisplayCurrency(c)} className={`px-3 py-1.5 rounded-md text-sm font-medium transition ${displayCurrency === c ? "bg-indigo-600 text-white" : "text-slate-500"}`} data-testid={`currency-${c}`}>
              {CURRENCY_SYMBOL[c]}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-1.5">
          <span className="font-mono2 text-[11px] text-slate-500">Tasa hoy</span>
          <input type="number" value={rate || ""} onChange={(e) => setRate(Number(e.target.value))} className="w-24 bg-slate-50 dark:bg-slate-900/60 border border-slate-200 dark:border-slate-800 rounded-md px-2 py-1 text-sm" placeholder="Bs/$" data-testid="movemap-rate" />
          <span className="font-mono2 text-[10px] text-slate-400" title="Los movimientos con fecha pasada se convierten con la tasa BCV de ese día.">Bs/$ · histórica por fecha ✓</span>
        </div>

        <select value={year} onChange={(e) => setYear(Number(e.target.value))} className="bg-slate-50 dark:bg-slate-900/60 border border-slate-200 dark:border-slate-800 rounded-lg px-3 py-2 text-sm" data-testid="movemap-year">
          {years.map((y) => <option key={y} value={y}>{y}</option>)}
        </select>

        <button onClick={() => { setEditing(null); setFormOpen(true); }} className={BTN_PRIMARY} data-testid="add-movement-button">
          <Plus size={16} /> Movimiento
        </button>
        <button onClick={() => setListOpen((v) => !v)} className={ICON_BTN} title="Lista de movimientos" data-testid="toggle-list">
          <ListTree size={16} />
        </button>
        <button onClick={undo} disabled={!canUndo} className={`${ICON_BTN} disabled:opacity-40`} title="Deshacer (Ctrl+Z)" data-testid="movemap-undo"><Undo2 size={16} /></button>
        <button onClick={redo} disabled={!canRedo} className={`${ICON_BTN} disabled:opacity-40`} title="Rehacer (Ctrl+Y)" data-testid="movemap-redo"><Redo2 size={16} /></button>
        <button onClick={save} disabled={saving} className={BTN_PRIMARY} data-testid="movemap-save">
          <Save size={16} /> {saving ? "Guardando..." : "Guardar"}
        </button>
      </div>

      <div className="flex-1 flex min-h-0">
        <div className="flex-1 relative">
          <ReactFlow
            nodes={nodes}
            edges={edges}
            nodeTypes={nodeTypes}
            fitView
            nodesDraggable={false}
            nodesConnectable={false}
            elementsSelectable={false}
            proOptions={{ hideAttribution: true }}
            minZoom={0.15}
          >
            <Background gap={22} size={1.5} color={bgColor} />
            <Controls showInteractive={false} />
          </ReactFlow>

          {totals.count === 0 && (
            <div className="absolute inset-x-0 bottom-6 flex items-end justify-center pointer-events-none px-4">
              <div className="text-center pointer-events-auto bg-white/90 dark:bg-slate-900/90 border border-slate-200 dark:border-slate-800 rounded-xl px-6 py-4 shadow-lg backdrop-blur-sm">
                <Wallet size={28} className="mx-auto text-slate-300 dark:text-slate-600 mb-2" />
                <p className="font-heading text-base mb-1">Sin movimientos en {year}</p>
                <p className="font-body text-sm text-slate-500 mb-3">Agrega tu primer ingreso o egreso y el mapa se construirá solo.</p>
                <button onClick={() => { setEditing(null); setFormOpen(true); }} className={BTN_PRIMARY} data-testid="empty-add-movement">
                  <Plus size={16} /> Agregar movimiento
                </button>
              </div>
            </div>
          )}
        </div>

        {listOpen && (
          <aside className="w-72 border-l border-slate-200 dark:border-slate-800 overflow-y-auto p-3 shrink-0" data-testid="movement-list">
            <div className="grid grid-cols-3 gap-2 mb-3">
              <div className="rounded-lg bg-emerald-50 dark:bg-emerald-950/40 p-2 text-center">
                <p className="font-mono2 text-[9px] text-emerald-600 dark:text-emerald-400">INGRESOS</p>
                <p className="font-mono2 text-[11px] font-semibold text-emerald-700 dark:text-emerald-300">{fmtMoney(totals.totalIn, displayCurrency)}</p>
              </div>
              <div className="rounded-lg bg-rose-50 dark:bg-rose-950/40 p-2 text-center">
                <p className="font-mono2 text-[9px] text-rose-600 dark:text-rose-400">EGRESOS</p>
                <p className="font-mono2 text-[11px] font-semibold text-rose-700 dark:text-rose-300">{fmtMoney(totals.totalEg, displayCurrency)}</p>
              </div>
              <div className="rounded-lg bg-slate-100 dark:bg-slate-800 p-2 text-center">
                <p className="font-mono2 text-[9px] text-slate-500">HABER</p>
                <p className={`font-mono2 text-[11px] font-semibold ${totals.haber >= 0 ? "text-slate-700 dark:text-slate-200" : "text-rose-600"}`}>{fmtMoney(totals.haber, displayCurrency)}</p>
              </div>
            </div>
            <p className="font-mono2 text-[11px] text-slate-400 mb-2">{monthList.length} movimiento(s) en {year}</p>
            <div className="flex flex-col gap-2">
              {monthList.map((m) => (
                <div key={m.id} className="group flex items-center gap-2 rounded-lg border border-slate-200 dark:border-slate-800 p-2" data-testid={`movement-item-${m.id}`}>
                  <span className={`w-2 h-2 rounded-full shrink-0 ${m.tipo === "ingreso" ? "bg-emerald-500" : "bg-rose-500"}`} />
                  <div className="flex-1 min-w-0">
                    <p className="font-body text-xs truncate">{m.concepto}</p>
                    {m.descripcion && <p className="font-body text-[10px] text-slate-400 truncate">{m.descripcion}</p>}
                    <p className="font-mono2 text-[10px] text-slate-400">{fmtMoney(m.monto, m.moneda)} · {parseLocalDate(m.fecha).toLocaleDateString("es-VE")}</p>
                  </div>
                  <button onClick={() => { setEditing(m); setFormOpen(true); }} className="opacity-70 sm:opacity-0 sm:group-hover:opacity-100 p-1 text-slate-400 hover:text-indigo-500" data-testid={`edit-movement-${m.id}`}><Pencil size={13} /></button>
                  <button onClick={() => removeMovement(m.id)} className="opacity-70 sm:opacity-0 sm:group-hover:opacity-100 p-1 text-slate-400 hover:text-rose-500" data-testid={`delete-movement-${m.id}`}><Trash2 size={13} /></button>
                </div>
              ))}
            </div>
          </aside>
        )}
      </div>

      {formOpen && (
        <MovementForm initial={editing} onSave={saveMovement} onCancel={() => { setFormOpen(false); setEditing(null); }} />
      )}
    </div>
  );
}

export default function MovementMap() {
  const { id } = useParams();
  return (
    <ReactFlowProvider>
      <Editor id={id} />
    </ReactFlowProvider>
  );
}
